import { useState, useEffect } from 'react';
import { CampaignQuestion } from '../CampaignQuestionsEditor/types';
import { generateTestValue, parseNumericValue, type FormulaTestValues } from './formulaUtils';

interface UseFormulaTestModeProps {
    referencedQuestions: Array<{ ref: string; questionIndex: number; question: CampaignQuestion }>;
    allQuestions: CampaignQuestion[];
}

interface UseFormulaTestModeReturn {
    testMode: boolean;
    testValues: FormulaTestValues;
    setTestMode: (enabled: boolean) => void;
    handleTestValueChange: (questionIndex: number, value: string) => void;
    resetTestValues: () => void;
}

/**
 * Custom hook to manage formula test mode state and test values.
 *
 * @param {UseFormulaTestModeProps} props
 * @returns {UseFormulaTestModeReturn}
 */
export const useFormulaTestMode = ({ referencedQuestions, allQuestions }: UseFormulaTestModeProps): UseFormulaTestModeReturn => {
    const [testMode, setTestMode] = useState(false);
    const [testValues, setTestValues] = useState<FormulaTestValues>({});

    const handleTestValueChange = (questionIndex: number, value: string) => {
        setTestValues(prev => ({
            ...prev,
            [questionIndex]: parseNumericValue(value),
        }));
    };

    const resetTestValues = () => {
        setTestValues({});
    };

    // Initialize test values when test mode is enabled
    useEffect(() => {
        if (testMode) {
            const newTestValues: FormulaTestValues = { ...testValues };
            referencedQuestions.forEach(refQuestion => {
                const questionIndex = refQuestion.questionIndex;
                if (testValues[questionIndex] === undefined) {
                    const question = allQuestions[questionIndex];
                    if (question) {
                        newTestValues[questionIndex] = generateTestValue(question, questionIndex);
                    }
                }
            });
            setTestValues(newTestValues);
        }
    }, [testMode, referencedQuestions]);

    return {
        testMode,
        testValues,
        setTestMode,
        handleTestValueChange,
        resetTestValues,
    };
};
